"use client";

import {
  Check,
  ChevronRight,
  Edit3,
  MessageCircle,
  MoreVertical,
  Trash2,
  X,
} from "lucide-react";
import { CommentEvents, trackEvent } from "@/analytics";
import {
  sanitizeContent,
  validateCommentContent,
} from "./utils/commentSecurity";
import { useEffect, useRef, useState } from "react";

import { Comment } from "./CommentSection";
import { CommentAuthorComedian } from "@punchup/data/models";
import CommentClapButton from "./CommentClapButton";
import CommentContent from "./CommentContent";
import CommentInput from "./CommentInput";
import Image from "next/image";
import Link from "next/link";
import { User } from "@supabase/supabase-js";
import { createThreadedReply } from "./utils/mentionUtils";
import dayjs from "dayjs";
import { getCDNImageURL } from "@punchup/utils";
import relativeTime from "dayjs/plugin/relativeTime";
import { useFeatureGate } from "statsig-react";
import { useManagedComedians } from "@/hooks/useManagedComedians";

dayjs.extend(relativeTime);

interface CommentItemProps {
  comment: Comment;
  currentUserId?: string;
  currentUser?: User | null;
  depth?: number;
  onReply?: (
    content: string,
    parentId: string,
    replyingToUsername?: string,
    authorComedian?: CommentAuthorComedian | null
  ) => void;
  onClap?: (commentId: string, clapCount: number) => Promise<void>;
  onEdit?: (commentId: string, newContent: string) => void;
  onDelete?: (commentId: string) => void;
  availableUsers?: Map<string, { id: string; username: string }>;
  // Optional tracking context
  specialId?: string;
  comedianId?: string;
  isLoggedIn?: boolean;
  userFollowsComedian?: boolean;
}

export default function CommentItem({
  comment,
  currentUserId,
  currentUser,
  depth = 0,
  onReply,
  onClap,
  onEdit,
  onDelete,
  availableUsers = new Map(),
  specialId,
  comedianId,
  isLoggedIn,
  userFollowsComedian,
}: CommentItemProps) {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [editError, setEditError] = useState<string | null>(null);
  const [showReplies, setShowReplies] = useState(false);

  // Handle reply toggle with better mobile support
  const handleToggleReplies = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const newShowReplies = !showReplies;
    setShowReplies(newShowReplies);

    // Track replies expand/collapse
    if (specialId && comedianId) {
      trackEvent(
        newShowReplies
          ? CommentEvents.REPLIES_EXPANDED
          : CommentEvents.REPLIES_COLLAPSED,
        {
          specialId,
          comedianId,
          commentId: comment.id,
          repliesCount: comment.replies?.length || 0,
          depth,
          isLoggedIn,
          userFollowsComedian,
        }
      );
    }
  };
  const [showActions, setShowActions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Feature gate for user profiles (under video_comments_stableid flag)
  const { gate: videoCommentsGate } = useFeatureGate("video_comments_stableid");
  const userProfilesEnabled = videoCommentsGate?.value ?? false;

  // Artists the current user manages — used to decide whether they can
  // edit/delete a comment that was authored "as the artist".
  const { managedComedianIds } = useManagedComedians();

  // A comment authored "as the artist" (author_comedian_id set, comedian joined).
  const authorComedian = comment.comedian ?? null;
  const isArtistComment = !!authorComedian;

  // Highlight the author as "the creator" only when the comment's artist is the
  // same comedian the content belongs to — not any artist a manager runs.
  const isContentCreatorComment =
    isArtistComment &&
    !!comedianId &&
    comment.author_comedian_id === comedianId;

  // Edit/delete is allowed for the original author, OR — for artist comments —
  // any manager of that artist (team ownership), mirroring the server rule.
  const isOwner =
    currentUserId === comment.user_id ||
    (isArtistComment &&
      !!comment.author_comedian_id &&
      managedComedianIds.includes(comment.author_comedian_id));
  const maxDepth = 1; // YouTube-style: only 2 visual levels (0=root, 1=replies), then flatten

  // If the artist this content belongs to replied anywhere in this thread,
  // surface their avatar next to the replies count so viewers see at a glance
  // that the author personally chimed in.
  const authorReply = comment.replies?.find(
    (reply) =>
      !!comedianId &&
      reply.author_comedian_id === comedianId &&
      !!reply.comedian
  );
  const authorReplyAvatarUrl = authorReply?.comedian?.about_img
    ? getCDNImageURL(authorReply.comedian.about_img)
    : null;

  // Get author display information.
  // Artist comments render the comedian identity; otherwise we render the user
  // (rich metadata for the current user, basic comment data for others).
  const getUserDisplayInfo = () => {
    if (authorComedian) {
      return {
        displayName: authorComedian.display_name,
        avatarUrl: authorComedian.about_img
          ? getCDNImageURL(authorComedian.about_img)
          : null,
      };
    }

    const isCurrentUser = currentUser && comment.user_id === currentUser.id;

    if (isCurrentUser && currentUser.user_metadata) {
      const metadata = currentUser.user_metadata;
      return {
        // Prioritize email prefix to match profile URLs, but keep rich metadata for avatar
        displayName:
          currentUser.email?.split("@")[0] ||
          metadata.full_name ||
          metadata.name ||
          "User",
        avatarUrl: metadata.avatar_url || metadata.picture || null,
      };
    }

    // Fallback to comment data for other users
    return {
      displayName: comment.users_extra?.username || "User",
      avatarUrl: null,
    };
  };

  const { displayName: userDisplayName, avatarUrl } = getUserDisplayInfo();
  const userInitial = userDisplayName.charAt(0).toUpperCase();

  // Where the author links to: artist page for artist comments, user profile otherwise.
  const authorHref = isArtistComment
    ? `/${authorComedian!.slug}`
    : `/u/${comment.user_id}`;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowActions(false);
      }
    };

    if (showActions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActions]);

  const handleReply = (
    content: string,
    authorComedian?: CommentAuthorComedian | null
  ) => {
    if (onReply) {
      // YouTube-style: All replies go to the root comment
      const rootCommentId = findRootCommentId(comment);

      // If replying to a reply (depth > 0), add @mention for context
      // BUT: Don't @mention yourself - that's weird and not how YouTube works!
      if (depth > 0 && comment.user_id !== currentUserId) {
        const threadedContent = createThreadedReply(userDisplayName, content);
        onReply(
          threadedContent,
          rootCommentId,
          userDisplayName,
          authorComedian
        );
      } else {
        // Direct reply to root comment OR replying to yourself - no @mention needed
        // When replying to yourself, treat it as a continuation of your own thread
        if (depth > 0 && comment.user_id === currentUserId) {
          // Self-reply in thread - goes to root but no @mention
          onReply(content, rootCommentId, userDisplayName, authorComedian);
        } else {
          // Direct reply to root comment
          onReply(content, comment.id, userDisplayName, authorComedian);
        }
      }
    }
    setShowReplyInput(false);
  };

  // Helper to find the root comment in the thread (traverse up the chain)
  const findRootCommentId = (currentComment: Comment): string => {
    // If this comment has no parent, it's the root
    if (!currentComment.parent_comment_id) {
      return currentComment.id;
    }
    // Otherwise, this is a reply - return the parent (which should be the root in YouTube model)
    return currentComment.parent_comment_id;
  };

  const handleEdit = () => {
    if (!onEdit) return;

    // Validate before submitting
    const validation = validateCommentContent(editContent);
    if (!validation.isValid) {
      setEditError(validation.errors.join(", "));
      return;
    }

    setEditError(null);
    onEdit(comment.id, editContent);
    setIsEditing(false);
  };

  const handleClap = async (commentId: string, clapCount: number) => {
    if (!onClap) return;

    // Track comment clap clicked
    if (specialId && comedianId) {
      trackEvent(CommentEvents.COMMENT_CLAP_CLICKED, {
        specialId,
        comedianId,
        commentId,
        clapCount,
        depth,
        isLoggedIn,
        userFollowsComedian,
      });
    }

    await onClap(commentId, clapCount);
  };

  const handleDelete = () => {
    if (!onDelete) return;

    // Track comment deletion
    if (specialId && comedianId) {
      trackEvent(CommentEvents.COMMENT_DELETED, {
        specialId,
        comedianId,
        commentId: comment.id,
        depth,
        contentLength: comment.content.length,
        isLoggedIn,
        userFollowsComedian,
      });
    }

    onDelete(comment.id);
  };

  return (
    <div className="flex space-x-3 group">
      {/* Avatar */}
      <div className="flex-shrink-0">
        {userProfilesEnabled ? (
          <Link href={authorHref}>
            {avatarUrl ? (
              <div className="w-10 h-10 overflow-hidden rounded-full hover:scale-105 transition-transform cursor-pointer">
                <Image
                  src={avatarUrl}
                  alt={userDisplayName}
                  width={40}
                  height={40}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center hover:scale-105 transition-transform cursor-pointer">
                <span className="text-sm font-semibold text-white">
                  {userInitial}
                </span>
              </div>
            )}
          </Link>
        ) : (
          <>
            {avatarUrl ? (
              <div className="w-10 h-10 overflow-hidden rounded-full">
                <Image
                  src={avatarUrl}
                  alt={userDisplayName}
                  width={40}
                  height={40}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-white">
                  {userInitial}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex flex-row items-center justify-between gap-x-2 mb-1">
          <div className="flex flex-row gap-x-2 items-center">
            {userProfilesEnabled || isArtistComment ? (
              <Link
                href={authorHref}
                className="font-semibold text-sm text-gray-900 hover:text-blue-600 transition-colors"
              >
                {isArtistComment ? userDisplayName : `@${userDisplayName}`}
              </Link>
            ) : (
              <h4 className="font-semibold text-sm text-gray-900">
                @{userDisplayName}
              </h4>
            )}
            {isArtistComment && authorComedian?.is_punchup_verified && (
              <span
                className="inline-flex items-center text-blue-500"
                title="Verified creator"
              >
                {/* Filled badge with a white checkmark (lucide BadgeCheck shape) */}
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"
                    fill="currentColor"
                  />
                  <path
                    d="m9 12 2 2 4-4"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
            {isContentCreatorComment && (
              <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                Author
              </span>
            )}
            <span className="text-xs text-gray-500">
              {dayjs(comment.created_at).fromNow()}
              {comment.is_edited && " (edited)"}
            </span>
          </div>
          {isOwner && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowActions(!showActions)}
                className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-3 -m-2 rounded-full hover:bg-gray-100 transition-opacity"
              >
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>
              {showActions && (
                <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setShowActions(false);
                    }}
                    className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => {
                      handleDelete();
                      setShowActions(false);
                    }}
                    className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Comment content */}
        {isEditing ? (
          <div className="mb-3">
            <textarea
              value={editContent}
              onChange={(e) => {
                setEditContent(e.target.value);
                setEditError(null); // Clear error on change
              }}
              className={`w-full px-0 py-2 border-b resize-none focus:outline-none transition-colors overflow-hidden border-gray-900 ${
                editError ? "border-red-500" : ""
              }`}
              rows={3}
              autoFocus
              maxLength={2000} // Client-side length limit
              style={{ minHeight: "60px" }}
            />
            {editError && (
              <p className="text-sm text-red-600 mt-1">{editError}</p>
            )}
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(comment.content);
                  setEditError(null);
                }}
                className="px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={editContent.trim().length === 0}
                className="px-4 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <CommentContent
            content={comment.content}
            availableUsers={availableUsers}
          />
        )}

        {/* Action buttons */}
        {!isEditing && (
          <div className="flex items-center space-x-4 text-xs">
            <CommentClapButton
              commentId={comment.id}
              initialClapCount={comment.clap_count || 0}
              initialUserClapCount={comment.user_clap_count || 0}
              authenticated={!!currentUserId}
              onClap={handleClap}
            />

            {currentUserId && (
              <button
                onClick={() => {
                  const willOpen = !showReplyInput;

                  // Track reply button clicked
                  if (specialId && comedianId) {
                    trackEvent(CommentEvents.REPLY_BUTTON_CLICKED, {
                      specialId,
                      comedianId,
                      commentId: comment.id,
                      depth,
                      willOpenInput: willOpen,
                      isLoggedIn,
                      userFollowsComedian,
                    });
                  }

                  setShowReplyInput(willOpen);
                }}
                className="flex items-center space-x-1 px-2 py-1 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Reply</span>
              </button>
            )}
          </div>
        )}

        {/* Reply input */}
        {showReplyInput && (
          <div className="mt-4">
            <CommentInput
              onSubmit={handleReply}
              placeholder={`Reply to @${userDisplayName}...`}
              autoFocus
              currentUser={currentUser}
              specialId={specialId}
              comedianId={comedianId}
              isLoggedIn={isLoggedIn}
              userFollowsComedian={userFollowsComedian}
              isReply={true}
              parentCommentId={comment.id}
            />
          </div>
        )}

        {/* Nested replies - only show for root comments (YouTube-style) */}
        {depth === 0 && comment.replies && comment.replies.length > 0 && (
          <div className="mt-3 md:mt-4">
            {comment.replies.length > 0 && (
              <button
                onClick={handleToggleReplies}
                onTouchEnd={handleToggleReplies}
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 mb-3 font-medium cursor-pointer touch-manipulation"
              >
                {authorReplyAvatarUrl ? (
                  <>
                    <span
                      className="w-5 h-5 overflow-hidden rounded-full flex-shrink-0"
                      title={`${authorReply!.comedian!.display_name} replied`}
                    >
                      <Image
                        src={authorReplyAvatarUrl}
                        alt={`${authorReply!.comedian!.display_name} replied`}
                        width={20}
                        height={20}
                        className="h-full w-full object-cover"
                      />
                    </span>
                    <span>
                      · {comment.replies.length} repl
                      {comment.replies.length === 1 ? "y" : "ies"}
                    </span>
                    <ChevronRight
                      className={`w-3.5 h-3.5 transition-transform ${
                        showReplies ? "rotate-90" : ""
                      }`}
                    />
                  </>
                ) : (
                  <span>
                    {showReplies ? "Hide" : "Show"} {comment.replies.length}{" "}
                    repl
                    {comment.replies.length === 1 ? "y" : "ies"}
                  </span>
                )}
              </button>
            )}

            {showReplies && (
              <div className="space-y-4">
                {comment.replies
                  .filter((reply) => reply && reply.id)
                  .map((reply) => (
                    <CommentItem
                      key={reply.id}
                      comment={reply}
                      currentUserId={currentUserId}
                      currentUser={currentUser}
                      depth={1}
                      onReply={onReply}
                      onClap={onClap}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      availableUsers={availableUsers}
                      specialId={specialId}
                      comedianId={comedianId}
                      isLoggedIn={isLoggedIn}
                      userFollowsComedian={userFollowsComedian}
                    />
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
